/**
 * CORE-1299 — куда вернуть человека, начавшего регистрацию из конкретного эфира.
 *
 * Адрес эфира и сегодня доезжает до формы: экраны Live зовут
 * `navigate('Login', { returnTo })`, а `LoginScreen` этот путь после входа
 * отрабатывает (CORE-1190/1198). Ломается всё на одной границе — подтверждении
 * почты. Ссылка из письма открывается новым документом (а обычно и новой
 * вкладкой), параметров маршрута там нет вовсе, и `ConfirmEmailScreen` уводит
 * на приветствие уже без назначения. Дальше «Войти в пространство Agapella»
 * ведёт на голый `/login`, а он — на главную. Именно это сняла QA 05–06.09.2026
 * на проде: Анфиса пришла по ссылке на комнату, завела аккаунт, подтвердила
 * адрес — и оказалась на главной, без всякого следа эфира.
 *
 * Поэтому назначение живёт не в состоянии дерева и не в параметрах маршрута, а
 * в хранилище: на вебе это `localStorage`, один на все вкладки того же
 * origin, — единственное место, которое переживает и перезагрузку, и открытие
 * письма в соседней вкладке.
 *
 * Что здесь сознательно НЕ хранится:
 *   - origin. Хранится только путь, поэтому возврат всегда остаётся на том
 *     адресе, где человек и был: прод не может увести на дев или стенд;
 *   - название эфира и имя мастера. Идентичность — это код или id из пути,
 *     всё остальное меняется и на роль ключа не годится;
 *   - что угодно из исходной строки. Путь СОБИРАЕТСЯ заново из распознанных
 *     кусков, а не переписывается как есть, поэтому в хранилище физически
 *     не может попасть ни чужой домен, ни лишний параметр из адреса.
 */
import { storage } from '../utils/storage';

const KEY = 'agape_live_return_v1';

/**
 * Сколько назначение считается относящимся к этой регистрации.
 *
 * Сутки — это «письмо открыли утром следующего дня», обычный и ожидаемый
 * случай. Но не «неделю назад посмотрел приглашение, сегодня завожу аккаунт
 * с главной»: там эфир давно прошёл, и увести туда — хуже, чем не уводить
 * никуда. Срок здесь — не защита, а срок годности: подмену контекста
 * предотвращает не он, а перезапись при каждом входе в форму и снятие после
 * возврата.
 */
export const LIVE_RETURN_TTL_MS = 24 * 60 * 60 * 1000;

export interface StoredLiveReturn {
    /** Внутренний путь назначения, собранный заново — см. заголовок файла. */
    path: string;
    markedAt: number;
}

/**
 * Параметры, которые эфиру нужны, чтобы открыться, и которые поэтому переживают
 * регистрацию вместе с ним.
 *
 * Ровно те два, что собирает `buildLiveAccessQuery`: `invite` — код
 * приглашения в закрытый эфир (CORE-721), `preview` — токен предпросмотра
 * неопубликованного. Без них приватный эфир после возврата ответит отказом, и
 * возврат окажется бессмысленным. Всё прочее из адреса отбрасывается: utm,
 * реферальные метки и любой параметр, о котором мы ничего не знаем, — назначение
 * не место для переноса чужих строк.
 */
const CARRIED_QUERY = ['invite', 'preview'] as const;

/** `/live/12`, `/room/G3gHRY`, `/live-session/join/…`, `/practice-rooms/session/…` */
const LIVE_ROUTES: ReadonlyArray<{ prefix: string; numeric?: boolean }> = [
    { prefix: '/live', numeric: true },
    { prefix: '/room' },
    { prefix: '/live-session/join' },
    { prefix: '/practice-rooms/session' },
];

/**
 * Путь исходного эфира или `null`, если это не он.
 *
 * Собирается заново из распознанного префикса и одного сегмента-идентификатора,
 * поэтому результат по построению не может быть ни внешним адресом
 * (`//evil.example`, `https://…`), ни путём с лишними сегментами: сегмент
 * идентификатора не содержит `/` вовсе. Открытого редиректа здесь нет не
 * потому, что он отфильтрован, а потому, что собрать его не из чего.
 */
export function matchLiveDestination(raw: string | null | undefined): string | null {
    if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null;
    const [pathWithQuery] = raw.split('#');
    const [rawPath, queryString = ''] = pathWithQuery.split('?');
    const path = rawPath.length > 1 && rawPath.endsWith('/') ? rawPath.slice(0, -1) : rawPath;

    for (const route of LIVE_ROUTES) {
        if (!path.startsWith(`${route.prefix}/`)) continue;
        const segment = path.slice(route.prefix.length + 1);
        if (!segment || segment.includes('/')) return null;

        let id: string;
        try {
            id = decodeURIComponent(segment);
        } catch {
            return null; // битая escape-последовательность — не идентификатор
        }
        // `.` и `..` — не идентификаторы, а обход пути: `/room/..` браузер
        // сведёт к `/`, и «возврат» тихо окажется главной.
        if (!id || id === '.' || id === '..') return null;
        if (route.numeric && !/^\d+$/.test(id)) return null;

        const query = new URLSearchParams(queryString);
        const carried = new URLSearchParams();
        for (const name of CARRIED_QUERY) {
            const value = query.get(name);
            if (value) carried.set(name, value);
        }
        const suffix = carried.toString();
        return `${route.prefix}/${encodeURIComponent(id)}${suffix ? `?${suffix}` : ''}`;
    }
    return null;
}

/** Свежо ли назначение. Отдельно от хранилища, чтобы срок проверялся тестом. */
export function isLiveReturnFresh(
    stored: StoredLiveReturn | null,
    now: number,
): boolean {
    if (!stored || typeof stored.path !== 'string') return false;
    if (!matchLiveDestination(stored.path)) return false;
    const markedAt = typeof stored.markedAt === 'number' ? stored.markedAt : 0;
    return now >= markedAt && now - markedAt < LIVE_RETURN_TTL_MS;
}

/**
 * Запомнить назначение — или забыть прежнее, если человек пришёл в форму НЕ из
 * эфира.
 *
 * Одна функция на оба исхода намеренно. Вызывается она там, где человек входит
 * в форму входа или регистрации, и «пришёл с главной» — такой же явный выбор
 * контекста, как «пришёл из комнаты»: позавчерашнее приглашение не вправе
 * увести его в чужой эфир. Ровно это и требует приёмка: «старое приглашение не
 * подменяет новый явно выбранный контекст».
 */
export async function rememberLiveReturn(raw: string | null | undefined): Promise<void> {
    const path = matchLiveDestination(raw);
    try {
        if (!path) {
            await storage.removeJSON(KEY);
            return;
        }
        await storage.setJSON<StoredLiveReturn>(KEY, { path, markedAt: Date.now() });
    } catch {
        // Приватный режим или переполненное хранилище: возврат просто не
        // сработает и человек окажется на главной — как до этой задачи.
        // Регистрацию из-за неудачной записи не ломаем, она первична.
    }
}

/** Назначение, если оно есть и не протухло. Читает, но не расходует. */
export async function readLiveReturn(): Promise<string | null> {
    const stored = await storage.getJSON<StoredLiveReturn>(KEY).catch(() => null);
    return isLiveReturnFresh(stored, Date.now()) ? stored!.path : null;
}

/**
 * Забыть назначение: возврат состоялся (или человек оказался в продукте другим
 * путём). Без этого следующий, уже обычный, вход с того же устройства уводил бы
 * в тот же старый эфир.
 */
export async function forgetLiveReturn(): Promise<void> {
    await storage.removeJSON(KEY).catch(() => {});
}
